import { useRef, useEffect, RefObject } from 'react'

interface TouchPoint {
  x: number
  y: number
  timestamp: number
}

interface SwipeGesture {
  direction: 'left' | 'right' | 'up' | 'down'
  distance: number
  velocity: number
  duration: number
}

interface TouchGestureOptions {
  onSwipe?: (gesture: SwipeGesture) => void
  onTap?: (point: TouchPoint) => void
  onLongPress?: (point: TouchPoint) => void
  minSwipeDistance?: number
  maxSwipeTime?: number
  longPressDelay?: number
  preventScroll?: boolean
}

export function useTouchGestures<T extends HTMLElement>(
  options: TouchGestureOptions = {}
): RefObject<T> {
  const elementRef = useRef<T>(null)
  const touchStartRef = useRef<TouchPoint | null>(null)
  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null)

  const {
    onSwipe,
    onTap,
    onLongPress,
    minSwipeDistance = 50,
    maxSwipeTime = 500,
    longPressDelay = 500,
    preventScroll = false,
  } = options

  useEffect(() => {
    const element = elementRef.current
    if (!element) return

    const handleTouchStart = (event: TouchEvent) => {
      if (preventScroll) {
        event.preventDefault()
      }

      const touch = event.touches[0]
      const touchPoint: TouchPoint = {
        x: touch.clientX,
        y: touch.clientY,
        timestamp: Date.now(),
      }

      touchStartRef.current = touchPoint

      // Start long press timer
      if (onLongPress) {
        longPressTimerRef.current = setTimeout(() => {
          onLongPress(touchPoint)
        }, longPressDelay)
      }
    }

    const handleTouchMove = (event: TouchEvent) => {
      if (preventScroll) {
        event.preventDefault()
      }

      // Cancel long press if finger moves
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current)
        longPressTimerRef.current = null
      }
    }

    const handleTouchEnd = (event: TouchEvent) => {
      if (preventScroll) {
        event.preventDefault()
      }

      // Cancel long press timer
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current)
        longPressTimerRef.current = null
      }

      const touchStart = touchStartRef.current
      if (!touchStart) return

      const touch = event.changedTouches[0]
      const touchEnd: TouchPoint = {
        x: touch.clientX,
        y: touch.clientY,
        timestamp: Date.now(),
      }

      const deltaX = touchEnd.x - touchStart.x
      const deltaY = touchEnd.y - touchStart.y
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY)
      const duration = touchEnd.timestamp - touchStart.timestamp

      // Check for tap
      if (distance < 10 && duration < 200 && onTap) {
        onTap(touchStart)
        return
      }

      // Check for swipe
      if (distance >= minSwipeDistance && duration <= maxSwipeTime && onSwipe) {
        const velocity = distance / duration
        let direction: SwipeGesture['direction']

        // Determine swipe direction
        if (Math.abs(deltaX) > Math.abs(deltaY)) {
          direction = deltaX > 0 ? 'right' : 'left'
        } else {
          direction = deltaY > 0 ? 'down' : 'up'
        }

        onSwipe({
          direction,
          distance,
          velocity,
          duration,
        })
      }

      touchStartRef.current = null
    }

    const handleTouchCancel = () => {
      // Cancel long press timer
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current)
        longPressTimerRef.current = null
      }
      touchStartRef.current = null
    }

    // Add event listeners with passive option for better performance
    element.addEventListener('touchstart', handleTouchStart, { passive: !preventScroll })
    element.addEventListener('touchmove', handleTouchMove, { passive: !preventScroll })
    element.addEventListener('touchend', handleTouchEnd, { passive: !preventScroll })
    element.addEventListener('touchcancel', handleTouchCancel, { passive: true })

    return () => {
      element.removeEventListener('touchstart', handleTouchStart)
      element.removeEventListener('touchmove', handleTouchMove)
      element.removeEventListener('touchend', handleTouchEnd)
      element.removeEventListener('touchcancel', handleTouchCancel)

      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current)
      }
    }
  }, [onSwipe, onTap, onLongPress, minSwipeDistance, maxSwipeTime, longPressDelay, preventScroll])

  return elementRef
}

// Hook for horizontal swipe navigation (useful for service cards)
export function useSwipeNavigation(
  onSwipeLeft?: () => void,
  onSwipeRight?: () => void,
  options: Omit<TouchGestureOptions, 'onSwipe'> = {}
) {
  return useTouchGestures({
    ...options,
    onSwipe: (gesture) => {
      if (gesture.direction === 'left' && onSwipeLeft) {
        onSwipeLeft()
      } else if (gesture.direction === 'right' && onSwipeRight) {
        onSwipeRight()
      }
    },
  })
}

// Hook for pull-to-refresh functionality
export function usePullToRefresh<T extends HTMLElement>(
  onRefresh: () => void,
  threshold: number = 100
): RefObject<T> {
  const elementRef = useRef<T>(null)
  const startYRef = useRef<number>(0)
  const isPullingRef = useRef<boolean>(false)

  useEffect(() => {
    const element = elementRef.current
    if (!element) return

    const handleTouchStart = (event: TouchEvent) => {
      if (element.scrollTop === 0) {
        startYRef.current = event.touches[0].clientY
        isPullingRef.current = true
      }
    }

    const handleTouchMove = (event: TouchEvent) => {
      if (!isPullingRef.current) return

      const currentY = event.touches[0].clientY
      const deltaY = currentY - startYRef.current

      if (deltaY > 0 && element.scrollTop === 0) {
        event.preventDefault()
        
        // Add visual feedback here if needed
        if (deltaY > threshold) {
          element.style.transform = `translateY(${Math.min(deltaY * 0.5, threshold)}px)`
        }
      }
    }

    const handleTouchEnd = (event: TouchEvent) => {
      if (!isPullingRef.current) return

      const endY = event.changedTouches[0].clientY
      const deltaY = endY - startYRef.current

      if (deltaY > threshold) {
        onRefresh()
      }

      // Reset visual state
      element.style.transform = ''
      isPullingRef.current = false
    }

    element.addEventListener('touchstart', handleTouchStart, { passive: false })
    element.addEventListener('touchmove', handleTouchMove, { passive: false })
    element.addEventListener('touchend', handleTouchEnd, { passive: true })

    return () => {
      element.removeEventListener('touchstart', handleTouchStart)
      element.removeEventListener('touchmove', handleTouchMove)
      element.removeEventListener('touchend', handleTouchEnd)
    }
  }, [onRefresh, threshold])

  return elementRef
}